import { useEffect, useState, type FormEvent } from "react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { Button } from "@/components/ui/Button";
import { toCommissionForm } from "@/features/commission-settings/api";
import { useCommissionSettings, useUpdateCommissionSettings } from "@/features/commission-settings/hooks";
import type { CommissionSettingsForm } from "@/features/commission-settings/types";

const emptyForm: CommissionSettingsForm = {
  commissionsOpen: false,
  headline: "",
  introText: "",
  processText: "",
  pricingText: "",
  turnaroundText: "",
  requirementsText: "",
  contactCtaText: "",
  contactCtaUrl: "",
  vehicleLiveriesEnabled: true,
  uniformDesignsEnabled: false,
  customDesignsEnabled: true,
};

export function CommissionSettingsPage() {
  const settings = useCommissionSettings();
  const updateSettings = useUpdateCommissionSettings();
  const [form, setForm] = useState<CommissionSettingsForm>(emptyForm);
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    if (settings.data) setForm(toCommissionForm(settings.data));
  }, [settings.data]);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage(null);
    updateSettings.mutate(form, {
      onSuccess: (data) => {
        setForm(toCommissionForm(data));
        setMessage({ tone: "success", text: "Commission settings saved." });
      },
      onError: (err) => setMessage({ tone: "error", text: err instanceof Error ? err.message : "Unable to save settings." }),
    });
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Commission Settings"
        description="Manage the singleton source of truth for commission availability and page copy."
        actions={<StatusBadge tone={form.commissionsOpen ? "active" : "inactive"}>{form.commissionsOpen ? "Open" : "Closed"}</StatusBadge>}
      />

      {settings.isLoading ? (
        <div className="rounded-md border border-cyan-300/15 bg-black/25 p-6 text-sm text-content-secondary">Loading commission settings...</div>
      ) : (
        <form className="space-y-6" onSubmit={handleSubmit}>
          <section className="rounded-md border border-cyan-300/15 bg-black/25 p-4">
            <h3 className="font-display text-lg font-semibold uppercase text-white">Availability</h3>
            <div className="mt-4 grid gap-4 md:grid-cols-3">
              <ToggleField
                label="Commissions Open"
                checked={form.commissionsOpen}
                onChange={(value) => setForm((current) => ({ ...current, commissionsOpen: value }))}
              />
              <ToggleField
                label="Vehicle Liveries"
                checked={form.vehicleLiveriesEnabled}
                onChange={(value) => setForm((current) => ({ ...current, vehicleLiveriesEnabled: value }))}
              />
              <ToggleField
                label="Uniform Designs"
                checked={form.uniformDesignsEnabled}
                onChange={(value) => setForm((current) => ({ ...current, uniformDesignsEnabled: value }))}
              />
              <ToggleField
                label="Custom Designs"
                checked={form.customDesignsEnabled}
                onChange={(value) => setForm((current) => ({ ...current, customDesignsEnabled: value }))}
              />
            </div>
          </section>

          <section className="grid gap-5 rounded-md border border-cyan-300/15 bg-black/25 p-4">
            <TextField label="Headline" value={form.headline} onChange={(value) => setForm((current) => ({ ...current, headline: value }))} />
            <TextArea label="Intro Text" value={form.introText} onChange={(value) => setForm((current) => ({ ...current, introText: value }))} />
            <TextArea label="Process Text" value={form.processText} onChange={(value) => setForm((current) => ({ ...current, processText: value }))} />
            <TextArea label="Pricing Text" value={form.pricingText} onChange={(value) => setForm((current) => ({ ...current, pricingText: value }))} />
            <TextArea label="Turnaround Text" value={form.turnaroundText} onChange={(value) => setForm((current) => ({ ...current, turnaroundText: value }))} />
            <TextArea
              label="Requirements Text"
              value={form.requirementsText}
              onChange={(value) => setForm((current) => ({ ...current, requirementsText: value }))}
            />
            <div className="grid gap-4 md:grid-cols-2">
              <TextField
                label="Contact CTA Text"
                value={form.contactCtaText}
                onChange={(value) => setForm((current) => ({ ...current, contactCtaText: value }))}
              />
              <TextField
                label="Contact CTA URL"
                value={form.contactCtaUrl}
                type="url"
                onChange={(value) => setForm((current) => ({ ...current, contactCtaUrl: value }))}
              />
            </div>
          </section>

          {message && (
            <p
              className={
                message.tone === "success"
                  ? "rounded-md border border-teal-300/30 bg-teal-300/10 px-3 py-2 text-sm text-teal-100"
                  : "rounded-md border border-red-300/30 bg-red-500/10 px-3 py-2 text-sm text-red-100"
              }
            >
              {message.text}
            </p>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={updateSettings.isPending}>
              {updateSettings.isPending ? "Saving..." : "Save Commission Settings"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "url";
}) {
  return (
    <label className="block">
      <span className="text-sm text-content-secondary">{label}</span>
      <input
        className="mt-2 h-11 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white outline-none transition focus:border-cyan-300"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        type={type}
      />
    </label>
  );
}

function TextArea({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block">
      <span className="text-sm text-content-secondary">{label}</span>
      <textarea
        className="mt-2 min-h-28 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 py-2 text-sm text-white outline-none transition focus:border-cyan-300"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function ToggleField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <label className="flex items-center gap-3 rounded-md border border-cyan-300/15 bg-black/20 p-3">
      <input className="size-4 accent-cyan-300" type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span className="text-sm text-content-secondary">{label}</span>
    </label>
  );
}
